/**
 * Floating action bar shown when items are selected.
 * Move + share wiring is completed in Phase 4; select/clear work now.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { IconX, IconChecks, IconArrowsMove, IconShare, IconDownload } from '@tabler/icons-react';
import { useGalleryStore } from '../../state/galleryStore.ts';
import { Spinner } from '../ui/Spinner.tsx';
import type { MediaItem } from '../../lib/types.ts';

interface BulkActionBarProps {
  event: string;
  items: MediaItem[];
  onMove?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  downloading?: boolean;
}

export function BulkActionBar({ items, onMove, onShare, onDownload, downloading }: BulkActionBarProps) {
  const selection = useGalleryStore((s) => s.selection);
  const selectMany = useGalleryStore((s) => s.selectMany);
  const clearSelection = useGalleryStore((s) => s.clearSelection);
  const count = selection.size;

  const allSelected = count > 0 && count === items.length;

  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          className="border border-line bg-surface shadow-md fixed bottom-5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-2xl px-3 py-2"
        >
          <span className="px-2 text-sm font-medium text-fg">
            {count} selected
          </span>
          <Divider />
          <Action
            label={allSelected ? 'Deselect all' : 'Select all'}
            onClick={() => (allSelected ? clearSelection() : selectMany(items.map((i) => i.key)))}
          >
            <IconChecks className="h-4 w-4" /> {allSelected ? 'None' : 'All'}
          </Action>
          {onDownload && (
            <Action
              label="Download selected"
              onClick={onDownload}
              disabled={downloading}
            >
              {downloading ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <IconDownload className="h-4 w-4" />
              )}{' '}
              Download
            </Action>
          )}
          {onShare && (
            <Action label="Share selected" onClick={onShare}>
              <IconShare className="h-4 w-4" /> Share
            </Action>
          )}
          {onMove && (
            <Action label="Move selected" onClick={onMove}>
              <IconArrowsMove className="h-4 w-4" /> Move
            </Action>
          )}
          <Divider />
          <button
            onClick={clearSelection}
            aria-label="Clear selection"
            className="grid h-11 w-11 place-items-center rounded-lg text-muted hover:bg-grid duration-200 ease-standard"
          >
            <IconX className="h-4 w-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Action({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="inline-flex h-11 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-fg-2 hover:bg-grid duration-200 ease-standard disabled:opacity-60"
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="h-5 w-px bg-line" />;
}
